import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  Play, Clock, Trophy, AlertTriangle, FileText, 
  CheckCircle2, Shield, Laptop, Calendar, Lock, Crown, Sparkles
} from "lucide-react";

const MockTests = ({ auth }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generatingTest, setGeneratingTest] = useState(null);
  const [mockTestHistory, setMockTestHistory] = useState([]);
  const [showRules, setShowRules] = useState(false);
  const [selectedMock, setSelectedMock] = useState(null);

  const isFreeUser = !auth.user || auth.user.subscription_tier === "free";

  useEffect(() => {
    fetchMockTestHistory();
  }, []);

  const fetchMockTestHistory = async () => {
    try {
      const response = await axios.get(`${API}/tests/history?type=mock`, {
        withCredentials: true,
        headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
      });
      setMockTestHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  };

  const mockTests = [
    {
      id: "official_mock_1",
      title: "Official Mock Test 1",
      subtitle: "Canadian Citizenship Test",
      description: "Full simulation of the official citizenship test with questions from all chapters",
      questions: 20,
      timeLimit: 45,
      passScore: 75,
      difficulty: "mixed",
      features: ["Random question selection", "Official test format", "Detailed score report"]
    },
    {
      id: "official_mock_2", 
      title: "Official Mock Test 2",
      subtitle: "Canadian Citizenship Test",
      description: "Alternative question set covering all aspects of the citizenship test",
      questions: 20,
      timeLimit: 45,
      passScore: 75,
      difficulty: "mixed",
      features: ["Different question pool", "Official test format", "Performance analysis"]
    },
    {
      id: "official_mock_3",
      title: "Official Mock Test 3",
      subtitle: "Canadian Citizenship Test",
      description: "Final practice test with challenging questions to ensure exam readiness",
      questions: 20,
      timeLimit: 45,
      passScore: 75,
      difficulty: "mixed",
      features: ["Challenge questions", "Time management practice", "Comprehensive review"]
    }
  ];

  const getTestAttempts = (testId) => {
    return mockTestHistory.filter(t => t.mock_test_id === testId);
  };

  const getBestScore = (testId) => {
    const attempts = getTestAttempts(testId);
    if (attempts.length === 0) return null;
    return Math.max(...attempts.map(a => a.score));
  };

  const handleStartTest = (mock) => {
    setSelectedMock(mock);
    setShowRules(true);
  };

  const startMockTest = async () => {
    if (!selectedMock) return;
    
    setGeneratingTest(selectedMock.id);
    try {
      const response = await axios.post(`${API}/mock-tests/generate`, {
        mock_test_id: selectedMock.id,
        question_count: selectedMock.questions,
        time_limit: selectedMock.timeLimit
      }, {
        withCredentials: true,
        headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
      });
      
      navigate(`/test/${response.data.test_id}`, { 
        state: { 
          questions: response.data.questions, 
          timeLimit: selectedMock.timeLimit,
          title: selectedMock.title,
          description: selectedMock.description,
          mockTestId: selectedMock.id,
          isOfficialMock: true,
          passScore: selectedMock.passScore
        } 
      });
    } catch (error) {
      toast.error("Failed to start mock test. Please try again.");
    } finally {
      setGeneratingTest(null);
      setShowRules(false);
    }
  };

  // Exam Rules Modal
  if (showRules && selectedMock) {
    return (
      <DashboardLayout auth={auth}>
        <div className="max-w-2xl mx-auto" data-testid="exam-rules">
          <Card className="border-2 border-maple shadow-xl">
            <CardHeader className="bg-maple text-white text-center py-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Shield className="w-6 h-6" />
                <span className="text-sm font-medium">IMMIGRATION, REFUGEES AND CITIZENSHIP CANADA</span>
              </div>
              <CardTitle className="font-heading text-2xl">Canadian Citizenship Test</CardTitle>
              <p className="text-maple-100 mt-1">Mock Examination</p>
            </CardHeader>
            <CardContent className="p-8">
              <div className="space-y-6">
                <div className="text-center border-b pb-6">
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                    {selectedMock.title}
                  </h3>
                  <p className="text-slate-600">{selectedMock.description}</p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                    Test Rules & Instructions
                  </h4>
                  <ul className="space-y-2 text-sm text-slate-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>You will have <strong>45 minutes</strong> to complete <strong>20 questions</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>You must score <strong>75% (15/20)</strong> or higher to pass</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Each question has only <strong>one correct answer</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>You can navigate between questions and change your answers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>The test will auto-submit when time expires</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Do not close or refresh the browser during the test</span>
                    </li>
                  </ul>
                </div>

                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-truenorth-50 rounded-lg p-3">
                    <FileText className="w-6 h-6 text-truenorth mx-auto mb-1" />
                    <p className="text-lg font-bold text-slate-900">20</p>
                    <p className="text-xs text-slate-600">Questions</p>
                  </div>
                  <div className="bg-maple-50 rounded-lg p-3">
                    <Clock className="w-6 h-6 text-maple mx-auto mb-1" />
                    <p className="text-lg font-bold text-slate-900">45</p>
                    <p className="text-xs text-slate-600">Minutes</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <Trophy className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-slate-900">75%</p>
                    <p className="text-xs text-slate-600">Pass Score</p>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowRules(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={startMockTest}
                    disabled={generatingTest === selectedMock.id}
                    className="flex-1 bg-maple hover:bg-maple-600"
                  >
                    {generatingTest === selectedMock.id ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Preparing...
                      </span>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Begin Test
                      </>
                    )}
                  </Button>
                </div>

                <p className="text-xs text-center text-slate-400">
                  By clicking "Begin Test", you acknowledge the rules above
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Free user paywall
  if (isFreeUser) {
    return (
      <DashboardLayout auth={auth}>
        <div className="space-y-8" data-testid="mock-tests-locked">
          {/* Official Header */}
          <div className="bg-maple text-white rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-medium uppercase tracking-wide">Official Practice Tests</span>
            </div>
            <h1 className="font-heading text-3xl font-bold text-center mb-2">
              Canadian Citizenship Mock Tests
            </h1>
            <p className="text-center text-maple-100 max-w-2xl mx-auto">
              Experience the real test format with timed mock exams.
            </p>
          </div>

          {/* Locked Banner */}
          <Card className="border-2 border-yellow-300 bg-gradient-to-r from-yellow-50 to-amber-50">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <Lock className="w-10 h-10 text-white" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">
                    Unlock 3 Realistic Mock Tests
                  </h2>
                  <p className="text-slate-600 mb-3">
                    Experience the exact format of the Canadian Citizenship Test. 
                    45-minute timer, 20 questions, 75% pass score - just like the real exam.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                    <Badge className="bg-white border border-slate-200">45-min Timer</Badge>
                    <Badge className="bg-white border border-slate-200">20 Questions Each</Badge>
                    <Badge className="bg-white border border-slate-200">Exam-like UI</Badge>
                    <Badge className="bg-white border border-slate-200">Score Report</Badge>
                  </div>
                </div>
                <div className="text-center">
                  <Link to="/pricing">
                    <Button className="bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-white font-semibold h-12 px-8">
                      <Crown className="w-5 h-5 mr-2" />
                      Unlock Now
                    </Button>
                  </Link>
                  <p className="text-xs text-slate-500 mt-2">
                    <span className="line-through">$29.99</span> → <span className="font-semibold text-red-600">$14.99</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Blurred Preview of Mock Tests */}
          <div className="relative">
            <div className="blur-[3px] opacity-60 pointer-events-none">
              <div className="grid md:grid-cols-3 gap-6">
                {mockTests.map((mock, index) => (
                  <Card key={mock.id} className="border-2 border-slate-200">
                    <CardHeader className="pb-2">
                      <Badge className="bg-maple text-white w-fit">Mock {index + 1}</Badge>
                      <CardTitle className="font-heading text-xl mt-2">{mock.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-600 mb-4">{mock.description}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1"><FileText className="w-4 h-4" /> 20 Qs</span>
                        <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> 45 min</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <Card className="border-2 border-yellow-400 shadow-xl bg-white/95 backdrop-blur max-w-md">
                <CardContent className="p-6 text-center">
                  <Sparkles className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">
                    Want to try something free first?
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Take our free Easy Simulation to get a feel for the question format!
                  </p>
                  <div className="flex gap-3">
                    <Link to="/simulations" className="flex-1">
                      <Button variant="outline" className="w-full">Free Simulation</Button>
                    </Link>
                    <Link to="/pricing" className="flex-1">
                      <Button className="w-full bg-yellow-500 hover:bg-yellow-600">
                        <Crown className="w-4 h-4 mr-2" />Upgrade
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout auth={auth}>
      <div className="space-y-8" data-testid="mock-tests-page">
        {/* Official Header */}
        <div className="bg-maple text-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-5 h-5" />
            <span className="text-sm font-medium uppercase tracking-wide">Official Practice Tests</span>
          </div>
          <h1 className="font-heading text-3xl font-bold text-center mb-2">
            Canadian Citizenship Mock Tests
          </h1>
          <p className="text-center text-maple-100 max-w-2xl mx-auto">
            Experience the real test format with timed mock exams. 
            Each test contains 20 questions and you have 45 minutes to complete it.
          </p>
        </div>

        {/* Test Info Banner */}
        <Card className="border-2 border-truenorth-100 bg-truenorth-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-6 justify-center flex-wrap">
              <div className="flex items-center gap-2">
                <Laptop className="w-5 h-5 text-truenorth" />
                <span className="text-sm font-medium">Computer-based format</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-truenorth" />
                <span className="text-sm font-medium">45 minutes duration</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-truenorth" />
                <span className="text-sm font-medium">20 multiple choice questions</span>
              </div>
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-truenorth" />
                <span className="text-sm font-medium">75% to pass (15/20)</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mock Tests Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {mockTests.map((mock, index) => {
            const attempts = getTestAttempts(mock.id);
            const bestScore = getBestScore(mock.id);
            const isPassed = bestScore !== null && bestScore >= mock.passScore;
            
            return (
              <Card 
                key={mock.id}
                className={`border-2 hover:shadow-lg transition-all ${
                  isPassed ? 'border-green-200 bg-green-50/30' : 'border-slate-200'
                }`}
                data-testid={`mock-test-${mock.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-maple text-white">Mock {index + 1}</Badge>
                    {isPassed && (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Passed
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="font-heading text-xl">{mock.title}</CardTitle>
                  <p className="text-sm text-slate-500">{mock.subtitle}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-600">{mock.description}</p>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1 text-slate-500">
                        <FileText className="w-4 h-4" /> {mock.questions} Qs
                      </span>
                      <span className="flex items-center gap-1 text-slate-500">
                        <Clock className="w-4 h-4" /> {mock.timeLimit} min
                      </span>
                    </div>
                  </div>

                  {attempts.length > 0 && (
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Best Score:</span>
                        <span className={`font-bold ${isPassed ? 'text-green-600' : 'text-red-600'}`}>
                          {bestScore}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-slate-600">Attempts:</span>
                        <span className="font-medium">{attempts.length}</span>
                      </div>
                    </div>
                  )}

                  <ul className="space-y-1">
                    {mock.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button 
                    onClick={() => handleStartTest(mock)}
                    className="w-full bg-maple hover:bg-maple-600"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {attempts.length > 0 ? 'Retake Test' : 'Start Test'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Tips Section */}
        <Card className="border-0 shadow-sm bg-slate-50">
          <CardContent className="p-6">
            <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">
              Tips for Success
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-maple rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Time Management</p>
                  <p className="text-sm text-slate-600">Aim for about 2 minutes per question. Don't spend too long on difficult ones.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-truenorth rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Read Carefully</p>
                  <p className="text-sm text-slate-600">Read each question and all options completely before selecting your answer.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Answer Everything</p>
                  <p className="text-sm text-slate-600">There's no penalty for wrong answers, so never leave a question blank.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Review Answers</p>
                  <p className="text-sm text-slate-600">If time permits, review your flagged questions before submitting.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default MockTests;
